import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useSiteContent } from '../hooks/useSiteContent';
import { normalizeLocale, useLanguage } from '../i18n/LanguageContext';
import { SiteLocale } from '../i18n/translations';
import { DEFAULT_NAV } from '../data/defaults';
import { onlineServicesHref } from '../utils/links';
import adanaLogo from './assets/adana_renkli.svg';
import iconPin from './assets/icon-pin.png';

const FALLBACK_LANGUAGES: { code: SiteLocale; label: string }[] = [
  { code: 'tr', label: 'TR' },
  { code: 'en', label: 'EN' },
];

function isExternal(path?: string) {
  return Boolean(path && /^(https?:)?\/\//i.test(path));
}

// Portalden gelen dil listesinde tr-TR, EN gibi degerler olabilir; tanimadigimiz
// kodlar (henuz icerigi olmayan diller) secilemez oldugu icin ayiklanir.
function usableLanguages(languages?: { code: string; label: string }[]) {
  const parsed = (languages || [])
    .map((item) => ({ code: normalizeLocale(item.code), label: item.label?.trim() }))
    .filter((item): item is { code: SiteLocale; label: string } => Boolean(item.code && item.label));

  const unique = parsed.filter(
    (item, index) => parsed.findIndex((other) => other.code === item.code) === index,
  );

  return unique.length > 0 ? unique : FALLBACK_LANGUAGES;
}

export function SiteHeader() {
  const { content } = useSiteContent();
  const { locale, setLocale, t } = useLanguage();
  const { settings } = content;
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const nav = settings.navigation?.length ? settings.navigation : DEFAULT_NAV;
  const languages = usableLanguages(settings.languages);

  return (
    <>
      <div className="topbar">
        <div className="container">
          <span className="topbar-address">
            <span className="topbar-pin" aria-hidden="true">
              <img src={iconPin} alt="" />
            </span>
            {settings.topbarAddress || settings.footerAddress}
          </span>
          <div className="topbar-actions">
            <div className="topbar-langs" role="group" aria-label={t('Dil')}>
              {languages.map((l) => (
                <button
                  type="button"
                  key={l.code}
                  lang={l.code}
                  className={`topbar-lang ${locale === l.code ? 'active' : ''}`}
                  aria-pressed={locale === l.code}
                  onClick={() => setLocale(l.code)}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <Link to="/iletisim">{t('İletişim')}</Link>
          </div>
        </div>
      </div>

      <header className="site">
        <div className="container nav-wrap">
          <div className="brand-block">
            <Link to="/" className="logo" onClick={() => setOpen(false)}>
              <img src={adanaLogo} alt="TBH Port Adana" className="logo-img" />
            </Link>
          </div>

          <nav className={`main ${open ? 'open' : ''}`}>
            <ul>
              {nav.map((item) => {
                const external = isExternal(item.path);
                const active =
                  !external &&
                  (location.pathname === item.path ||
                    (item.path !== '/' && location.pathname.startsWith(item.path)));
                return (
                  <li key={item.path}>
                    {external ? (
                      <a href={item.path} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>
                        {t(item.label)}
                      </a>
                    ) : (
                      <NavLink
                        to={item.path}
                        end={item.path === '/'}
                        onClick={() => setOpen(false)}
                        className={active ? 'active' : ''}
                      >
                        {t(item.label)}
                      </NavLink>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          <a
            href={onlineServicesHref(settings.onlineServicesUrl)}
            target="_blank"
            rel="noreferrer"
            className="btn btn-red"
          >
            {t('Online Hizmetler')}
          </a>
          <button
            className="nav-toggle"
            type="button"
            aria-label={t('Menü')}
            onClick={() => setOpen((v) => !v)}
          >
            &#9776;
          </button>
        </div>
      </header>
    </>
  );
}
